/**
 * SoulCopilotChat — ADR-150 D2. Authoring an agent's layer-4 SOUL by chat.
 *
 * The owner asked for parity with the workflow editor: "a skill that could
 * facilitate these changes via a chat interface to create and modify the soul of
 * any agent (like we have done with workflows)". So this deliberately mirrors
 * SkillStudio's copilot pane — the closest sibling — rather than the heavier
 * docked assistant, because a SOUL has no canvas to protect and a third distinct
 * chat idiom in one editor is a cost with no payoff.
 *
 * Two behaviours are not cosmetic:
 *
 *   A REPLY WITH NO DOCUMENT IS AN ANSWER, not a failure. The turn adopts a draft
 *   only when the server says `draft_changed`, so asking a question can never
 *   blank what the operator has written — the wipe the workflow copilot shipped
 *   with and had to fix.
 *
 *   THE CHAT IS DURABLE (ADR-137, extended to this surface by ADR-150 D2). The
 *   first cut of this pane had no history, so a refresh lost the conversation —
 *   which is verbatim the defect ADR-137 was written about, reappearing because
 *   the catalog had been named after its first caller. Turns persist as they
 *   happen and the newest conversation is adopted on open.
 *
 *   THE TURN CARRIES ITS OWN REVIEW. The copilot repairs against the same
 *   override check the propose gate runs, so the verdict that comes back with a
 *   draft is authoritative; the parent lifts it straight into the review panel
 *   instead of waiting for the debounced re-check to catch up and briefly showing
 *   a stale one.
 */
import { useEffect, useRef, useState } from 'react'
import { api, CopilotNotConfiguredError, UnauthorizedError } from '../api/client'
import type { ConversationSummary, SoulReviewResult } from '../api/client'
import { useAttentionNotify } from '../context/AttentionContext'
import { copilotJobAttentionId, localAttentionId } from '../utils/attentionStore'

type Turn = { role: 'user' | 'assistant'; content: string }

function em(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

export function SoulCopilotChat({
  agent,
  draft,
  onDraft,
  onReview,
  disabled,
  self,
}: {
  agent: string
  draft: string
  onDraft: (next: string) => void
  onReview: (r: SoulReviewResult) => void
  disabled?: boolean
  /** The signed-in operator — whose conversation is adopted on open (ADR-137 D8). */
  self: string
}) {
  const notifyAttention = useAttentionNotify()
  const [chat, setChat] = useState<Turn[]>([])
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  // ADR-137 D1: one draft, MANY conversations. `activeId` is which one this pane
  // is appending to; null means the next turn starts a new one.
  const [activeId, setActiveId] = useState<string | null>(null)
  const [history, setHistory] = useState<ConversationSummary[]>([])
  const [showHistory, setShowHistory] = useState(false)
  // Guards the adopt-on-open against overwriting a conversation the operator has
  // already started in this session — the same race the workflow dock handles.
  const adopted = useRef(false)

  // Load the catalog and adopt the newest of MY OWN (ADR-137 D8: the catalog is
  // fleet-wide, but landing in a colleague's conversation is a thing you choose,
  // not something that happens on open).
  useEffect(() => {
    let cancelled = false
    void api.listSoulConversations(agent, 'all')
      .then(list => {
        if (cancelled) return
        setHistory(list)
        if (adopted.current || chat.length > 0) return
        adopted.current = true
        const mine = list.find(c => c.operator === self)
        if (!mine) return
        return api.getSoulConversation(agent, mine.id).then(conv => {
          if (cancelled) return
          setActiveId(conv.id)
          setChat(conv.messages.map(m => ({ role: m.role === 'assistant' ? 'assistant' : 'user', content: m.content })))
        })
      })
      .catch(() => { /* a catalog that will not load is not a broken editor */ })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent])

  /** Persist the turn. Best-effort and AFTER the reply is on screen: a failed
   *  save must not lose the operator the answer they just got. */
  const persist = async (turns: Turn[]) => {
    try {
      const r = await api.saveSoulConversation(agent, {
        ...(activeId ? { id: activeId } : {}),
        messages: turns.map(t => ({ role: t.role, content: t.content })),
      })
      setActiveId(r.id)
      setHistory(await api.listSoulConversations(agent, 'all'))
    } catch { /* the chat is still on screen; the next turn retries */ }
  }

  const resume = async (id: string) => {
    try {
      const conv = await api.getSoulConversation(agent, id)
      setActiveId(conv.id)
      setChat(conv.messages.map(m => ({ role: m.role === 'assistant' ? 'assistant' : 'user', content: m.content })))
      setShowHistory(false)
    } catch (e) { setErr(em(e)) }
  }

  /** ADR-137 D4 — start fresh without losing the old one. */
  const newChat = () => {
    setActiveId(null)
    setChat([])
    setShowHistory(false)
  }

  const send = async () => {
    const msg = input.trim()
    if (!msg || busy) return
    const next: Turn[] = [...chat, { role: 'user', content: msg }]
    setChat(next)
    setInput('')
    setBusy(true)
    setErr(null)
    try {
      const r = await api.soulCopilot(agent, next, draft)
      // Adopt ONLY when the server says there is a document. `answer` turns and
      // failed turns both arrive with an empty draft.
      if (r.draft_changed && r.draft) onDraft(r.draft)
      if (r.review) onReview(r.review)
      const done: Turn[] = [...next, {
        role: 'assistant',
        content: r.answer || r.rationale || '(updated the persona)',
      }]
      setChat(done)
      notifyAttention({ id: copilotJobAttentionId(r, 'soul-copilot'), source: 'soul-copilot', reason: 'new-reply' })
      void persist(done)
    } catch (e) {
      setErr(e instanceof CopilotNotConfiguredError
        ? "The assistant isn't configured for this fleet."
        : em(e))
      if (!(e instanceof UnauthorizedError)) {
        notifyAttention({ id: localAttentionId('soul-copilot'), source: 'soul-copilot', reason: 'new-reply' })
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="rounded border border-gray-200 bg-white">
      <div className="border-b border-gray-100 px-3 py-1.5">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Assistant</span>
        <span className="ml-2 text-[11px] text-gray-400">
          knows what core governs — ask it to write {agent}&rsquo;s character
        </span>
        <span className="float-right flex items-center gap-2">
          {history.length > 0 && (
            <button type="button" onClick={() => setShowHistory(v => !v)}
              className="text-[11px] text-gray-500 hover:text-gray-800">
              History ({history.length})
            </button>
          )}
          <button type="button" onClick={newChat}
            className="text-[11px] text-gray-500 hover:text-gray-800"
            title="Start a fresh conversation. This one stays in History.">
            + New chat
          </button>
        </span>
      </div>

      {showHistory && (
        <ul className="max-h-48 overflow-auto divide-y divide-gray-100 border-b border-gray-100">
          {history.map(c => (
            <li key={c.id} className="flex items-center gap-2 px-3 py-1.5 text-xs">
              <button type="button" onClick={() => void resume(c.id)} className="flex-1 min-w-0 text-left">
                <span className="block truncate text-gray-800">{c.title || 'Untitled conversation'}</span>
                <span className="block text-[10px] text-gray-400">
                  {c.turns} turn{c.turns === 1 ? '' : 's'}
                  {c.operator && c.operator !== self && ` · ${c.operator.split('@')[0]}`}
                  {c.id === activeId && ' · open'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {chat.length > 0 && (
        <ul className="max-h-64 overflow-auto divide-y divide-gray-100">
          {chat.map((t, i) => (
            <li key={i} className="px-3 py-2 text-xs">
              <span className={`block text-[10px] uppercase tracking-wider ${t.role === 'user' ? 'text-gray-400' : 'text-indigo-500'}`}>
                {t.role === 'user' ? 'you' : 'assistant'}
              </span>
              <span className="block whitespace-pre-wrap text-gray-800">{t.content}</span>
            </li>
          ))}
        </ul>
      )}

      {err && <p className="px-3 py-2 text-xs text-red-600 whitespace-pre-wrap">{err}</p>}

      <div className="flex items-center gap-2 border-t border-gray-100 px-3 py-2">
        <input
          type="text"
          value={input}
          disabled={disabled || busy}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send() } }}
          placeholder={busy ? 'Working…' : `Describe ${agent}'s voice, or ask a question`}
          className="flex-1 text-xs border border-gray-300 rounded px-2 py-1 disabled:bg-gray-50"
        />
        <button
          type="button"
          disabled={disabled || busy || !input.trim()}
          onClick={() => void send()}
          className="px-2.5 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
        >
          {busy ? '…' : 'Send'}
        </button>
      </div>
    </div>
  )
}
